/**
 * Generador de TXT para Declaración IVA (Seniat)
 * Formato estándar tabulado para retenciones de IVA
 * Basado en lógica legacy (txt.java)
 */

export const generateIVATxt = (retenciones = [], empresa = {}) => {
    // Validar datos mínimos
    if (!retenciones || retenciones.length === 0) {
        throw new Error('No hay retenciones para generar el TXT');
    }
    if (!empresa || !empresa.rif) {
        throw new Error('Datos de empresa insuficientes (RIF requerido)');
    }

    const output = [];
    const rifAgente = empresa.rif.replace(/-/g, '');

    // Formateadores
    const formatNumber = (num) => {
        // Formato #####0.00, con punto decimal
        return parseFloat(num || 0).toFixed(2);
    };

    const formatPeriodo = (periodo) => {
        // Remover guiones: YYYY-MM -> YYYYMM
        return (periodo || '').replace(/-/g, '');
    };

    const formatDate = (dateStr) => {
        // Fecha original YYYY-MM-DD -> YYYY-MM-DD (El legacy java usa rs.getDate que devuelve YYYY-MM-DD)
        // El TXT legacy imprime fecha_doc directo. Si es Date Object usa .toString() por defecto o .format() si se aplica?
        // En el legacy 'fecha_doc.addItem(rs.getDate("retencion_iva.fecha"))' -> rs.getDate devuelve SQL Date.
        // Al imprimir en PW: pw.print(fecha_doc.getItemAt(i)+"\t") -> toString() de SQL Date es YYYY-MM-DD.
        // Así que mantenemos YYYY-MM-DD.
        if (!dateStr) return '';
        if (dateStr instanceof Date) {
            return dateStr.toISOString().split('T')[0];
        }
        return dateStr.split('T')[0]; // Asegurar YYYY-MM-DD
    };

    const formatTipoDoc = (idTipo) => {
        // Formato 00
        return String(idTipo || 0).padStart(2, '0');
    };

    retenciones.forEach(ret => {
        const item = { ...ret }; // Copia para no mutar original

        // Datos básicos
        const periodoFiscal = formatPeriodo(item.periodo_fiscal || item.periodoFiscal);
        const fechaDoc = formatDate(item.fecha_doc || item.fecha); // C Fecha Doc
        const tipoOp = 'C'; // D Tipo de Operación (Fijo C segun legacy)
        const tipoDoc = formatTipoDoc(item.tipo_idtipo || item.tipoDoc); // E Tipo Doc (01, 02, 03)
        const rifProv = (item.proveedor_rif || item.rif_proveedor || '').replace(/-/g, ''); // F Rif Prov
        const numDoc = item.numero_documento || item.numero || item.numeroFactura || ''; // G Num Doc
        const numControl = (item.numero_control || item.numeroControl || '').replace(/-/g, ''); // H Num Control
        const docAfectado = item.doc_afectado || '0'; // L Doc Afectado
        const numComp = periodoFiscal + (item.idcabezeras_iva || item.id); // M Num Comprobante (Periodo + ID)
        const alicuota = formatNumber(item.iva_alicuota || item.alicuota); // O Alicuota

        let totalDoc = 0;
        let base = parseFloat(item.base) || 0;
        let exento = parseFloat(item.exento) || 0;
        let ivaAlicuota = parseFloat(item.iva_alicuota || item.alicuota) || 0;
        let retenido = parseFloat(item.retenido) || 0;

        // Calcular valores consistentes si faltan
        // Legacy calculation:
        // totalDoc = exento + (base * (1 + alicuota/100))
        // montoRet = base * (alicuota/100) * (retencion_proveedor/100)

        // Usamos los valores directos si existen, sino recalculamos como legacy
        const calcImpuesto = base * (ivaAlicuota / 100);
        totalDoc = exento + base + calcImpuesto;

        // Manejo de Nota de Crédito (tipo_idtipo == 3 o similar)
        // El legacy chequea: if(rs.getInt("retencion_iva.tipo_idtipo")!=3)
        // Si es 3 (NC) -> Multiplica por -1
        const isNC = parseInt(item.tipo_idtipo) === 3 || String(item.tipo_documento).includes('Credit');

        if (isNC) {
            totalDoc = totalDoc * -1;
            base = base * -1;
            retenido = retenido * -1;
            exento = exento < 0 ? Math.abs(exento) : 0; // Legacy logic: if(exento<0) exento = exento * -1 else 0? 
            // Legacy logic for exento in NC:
            // if(rs.getDouble("retencion_iva.exento")<0) exento = (rs.getDouble("retencion_iva.exento"))*(-1);
            // Esto es raro, si es negativo lo hace positivo? Sí, para mostrarlo en columna exento.
            // Pero totalDoc y Base van negativos.
            if (parseFloat(item.exento) < 0) exento = Math.abs(parseFloat(item.exento));
        }

        // Construir línea tabulada
        // Orden: RifAgente, Periodo, Fecha, TipoOp, TipoDoc, RifProv, NumDoc, NumControl, TotalDoc, Base, Retenido, DocAfectado, NumComp, Exento, Alicuota, 0
        const line = [
            rifAgente, // A
            periodoFiscal, // B
            fechaDoc, // C
            tipoOp, // D
            tipoDoc, // E
            rifProv, // F
            numDoc, // G
            numControl, // H
            formatNumber(totalDoc), // I
            formatNumber(base), // J
            formatNumber(retenido), // K
            docAfectado, // L
            numComp, // M
            formatNumber(exento), // N 
            alicuota, // O
            '0' // P Num Ext (Fijo 0)
        ].join('\t');

        output.push(line);
    });

    return output.join('\n');
};
